import { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine } from 'recharts';
import type { Lead } from '../../services/api/leads';
import { formatDisplayDate } from '../../utils/format';
import { formatMonthYear2Digit } from '../../utils/dateFormat';
import { LEGACY_CZ_NUMBER_LOCALE, numberFormatter } from '../../utils/localeFormat';

interface ProjectTimelineProps {
  leads: Lead[];
  onLeadClick: (lead: Lead) => void;
}

const COLORS = [
  '#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6',
  '#06b6d4', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

function monthDiff(start: string, end: string): number {
  const s = new Date(start);
  const e = new Date(end);
  return (e.getUTCFullYear() - s.getUTCFullYear()) * 12 + (e.getUTCMonth() - s.getUTCMonth()) + 1;
}

function monthsBefore(date: string, earliest: string): number {
  const d = new Date(date);
  const e = new Date(earliest);
  return (d.getUTCFullYear() - e.getUTCFullYear()) * 12 + (d.getUTCMonth() - e.getUTCMonth());
}

function formatMonth(monthOffset: number, earliest: string): string {
  const e = new Date(earliest);
  const d = new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth() + monthOffset, 1));
  return formatMonthYear2Digit(d); // MKS-351 — "Oct '26", English month
}

function formatCZK(value: number): string {
  if (!value) return '';
  return numberFormatter({ style: 'currency', currency: 'CZK', maximumFractionDigits: 0 }, LEGACY_CZ_NUMBER_LOCALE).format(value);
}

export default function ProjectTimeline({ leads, onLeadClick }: ProjectTimelineProps) {
  const { chartData, earliestMonth, totalMonths, todayOffset } = useMemo(() => {
    const wonLeads = leads.filter(
      // MKS-345 — reads the stage's kind, not its name.
      (l) => l.stageKind === 'won' && l.expectedStartDate && l.expectedEndDate
    );

    if (wonLeads.length === 0) return { chartData: [], earliestMonth: '', totalMonths: 0, todayOffset: 0 };

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;

    // Find earliest start and latest end
    const starts = wonLeads.map((l) => l.expectedStartDate!);
    const ends = wonLeads.map((l) => l.expectedEndDate!);
    // Start from whichever is earlier: today or the earliest project
    const earliest = [todayStr, ...starts].sort()[0]!;
    const latest = ends.sort().reverse()[0]!;
    const totalMonths = monthDiff(earliest, latest);
    const todayOffset = monthsBefore(todayStr, earliest);

    const chartData = wonLeads.map((lead, i) => ({
      name: lead.customer?.name || lead.name,
      lead,
      offset: monthsBefore(lead.expectedStartDate!, earliest),
      duration: monthDiff(lead.expectedStartDate!, lead.expectedEndDate!),
      value: parseFloat(lead.dealValue ?? '0'),
      color: COLORS[i % COLORS.length]!,
    }));

    return { chartData, earliestMonth: earliest, totalMonths, todayOffset };
  }, [leads]);

  if (chartData.length === 0) return null;

  // Build ticks for X axis
  const ticks = Array.from({ length: totalMonths }, (_, i) => i);

  return (
    <div className="mt-8 bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-2">Project Timeline</h2>
      <p className="text-sm text-gray-500 mb-4">Won deals by expected project duration</p>
      <ResponsiveContainer width="100%" height={Math.max(200, chartData.length * 50 + 60)}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 20, left: 20, bottom: 20 }}
          barSize={24}
        >
          <XAxis
            type="number"
            domain={[0, totalMonths]}
            ticks={ticks}
            tickFormatter={(v: number) => formatMonth(v, earliestMonth)}
            tick={{ fontSize: 11, fill: '#6b7280' }}
            interval={0}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={150}
            tick={{ fontSize: 12, fill: '#374151' }}
          />
          <Tooltip
            cursor={false}
            content={({ active, payload }) => {
              if (!active || !payload?.[0]) return null;
              const d = payload[0].payload;
              return (
                <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
                  <p className="font-semibold text-gray-900">{d.name}</p>
                  <p className="text-gray-500">{formatDisplayDate(d.lead.expectedStartDate)} &rarr; {formatDisplayDate(d.lead.expectedEndDate)}</p>
                  <p className="text-gray-500">{d.duration} months</p>
                  {d.value > 0 && <p className="text-gray-700 font-medium">{formatCZK(d.value)}</p>}
                </div>
              );
            }}
          />
          <ReferenceLine x={todayOffset} stroke="#ef4444" strokeWidth={2} strokeDasharray="4 3" label={{ value: 'Today', position: 'top', fontSize: 11, fill: '#ef4444' }} />
          {/* Invisible offset bar */}
          <Bar dataKey="offset" stackId="gantt" fill="transparent" isAnimationActive={false} />
          {/* Visible duration bar */}
          <Bar
            dataKey="duration"
            stackId="gantt"
            radius={[4, 4, 4, 4]}
            cursor="pointer"
            onClick={(_data, index) => {
              if (typeof index === 'number' && chartData[index]) onLeadClick(chartData[index].lead);
            }}
          >
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
